import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { ButtonSize, RegularButton } from "@/stories/button/button";
import { postOrderDetailsByISIN } from "@/app/api/service/stocks";
import { PaymentMethod } from "./payment-step";
import { useTransactionFlow } from "@/context/TransactionContext";
import { ACTIONS } from "../actions";
import { generateUUID } from "@/app/utils/helper";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import Image from "next/image";
import useMobile from "@/app/hooks/use-mobile";

interface NEFTModalProps {
    isAdvisoryFlow?: boolean;
}

const NEFTModal: React.FC<NEFTModalProps> = ({ isAdvisoryFlow }) => {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = new URLSearchParams(useSearchParams());
    const { isMobile } = useMobile();

    const { dispatch, state } = useTransactionFlow();
    const [activeStep, setActiveStep] = useState(1);
    const [loopDone, setLoopDone] = useState(false);

    const appendUUIDToURL = () => {
        const newUUID = generateUUID();
        searchParams.set("ref", newUUID);
        router.replace(`${pathname}?${searchParams.toString()}`, { scroll: false });
    };

    useEffect(() => {
        if (loopDone) {
            setActiveStep(0); // Reset active step when loop is done
            return;
        }

        const interval = setInterval(() => {
            setActiveStep((prev) => {
                if (prev < steps.length) {
                    return prev + 1;
                } else {
                    setLoopDone(true);
                    clearInterval(interval);
                    return 0; // Reset to 0 when loop completes
                }
            });
        }, 1500);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loopDone]);

    const handleOrder = () => {
        const payload = {
            paymentType: PaymentMethod.NEFT,
        };

        postOrderDetailsByISIN({
            isin: state.isin ?? "",
            orderId: state.orderId ?? "",
            payload,
        }).then((res) => {
            if (res.success) {
                appendUUIDToURL();

                if (isAdvisoryFlow) {
                    window.location.replace(
                        `${process.env.ADVISORY_URL}/orders/${state?.orderId}/payments/status?status=success`,
                    );
                } else {
                    dispatch({ type: ACTIONS.SET_COMPLETED });
                }
            } else {
                if (isAdvisoryFlow) {
                    window.location.replace(
                        `${process.env.ADVISORY_URL}/orders/${state?.orderId}/payments/status?status=failed`,
                    );
                } else {
                    dispatch({ type: ACTIONS.SET_FAILED });
                }
            }
        });
    };

    const steps = [
        {
            id: 1,
            title: "Login to your Bank Account through<br/> Netbanking or Mobile App",
        },
        {
            id: 2,
            title: "Add Beneficiary Details",
            content: [
                {
                    label: "Beneficiary Account Number",
                    value: `Current - ${state.NeftDetails?.accountNumber}` || "--",
                },
                {
                    label: "Beneficiary Name",
                    value: /* state.NeftDetails?.name || "--" */ "Alpha Fintech Pvt Ltd",
                },
                { label: "IFSC Code", value: state?.NeftDetails?.ifsc || "--" },
                {
                    label: "Branch details",
                    value: /* state?.NeftDetails?.branchName || */ "Bengaluru",
                },
                {
                    label: "Bank name",
                    value: /* state?.NeftDetails?.bankName || "--" */ "Axis Bank",
                },
            ],
        },
        {
            id: 3,
            title: "Beneficiary registration may take up to 24 hours",
        },
        {
            id: 4,
            title: "Once beneficiary registration is completed, you can initiate funds transfer",
        },
    ];

    const formattedExpireAt = state?.postDigioTxnData?.orderExpireAt
        ? new Date(state.postDigioTxnData.orderExpireAt)
              .toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
              })
              .replace(",", "")
              .replace(":", ".")
              .replace(/\bam\b/, "AM")
              .replace(/\bpm\b/, "PM")
        : "";



    return (
        <Dialog open={true}>
            <DialogContent className="h-[90vh] scale-90 overflow-auto !rounded-3xl md:h-auto md:min-w-[617px] md:max-w-[617px] md:scale-75 md:px-s10 md:pb-s9 md:pt-s10 [&>button:last-child]:hidden">
                <VisuallyHidden.Root>
                    <DialogTitle>NEFT Modal</DialogTitle>
                </VisuallyHidden.Root>
                <div className="w-full">
                    <div className="w-full">
                        <div className="flex flex-col justify-center">
                            <Image
                                src={`https://assets.incredmoney.com/images/webp/incred-money.webp`}
                                alt="InCred Money"
                                height={100}
                                width={100}
                                className="mb-s5 h-[32px] w-[90px] cursor-pointer self-start sm:h-[42px] sm:w-[119px] md:mb-s8"
                            />
                            <p className="text-left text-xl font-semibold leading-[31.2px] tracking-[-0.24px] md:text-3xl">
                                NEFT/RTGS Payment Instructions
                            </p>
                            <p className="mb-s6 text-xs leading-[19.2px] tracking-[0.12px] text-neutral-500 md:mb-s8">
                                <span className="font-semibold">Note:</span> Our NEFT/RTGS details
                                have changed. Please update the IFSC Code to UTIB000RAZP. Other
                                details will remain the same.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="mx-0 w-full">
                            <div className="relative">
                                {/* Vertical Line */}
                                <div className="absolute left-[17px] top-5 h-[calc(100%-60px)] w-[6px] bg-neutral-100" />

                                {/* Steps */}
                                {steps.map((step, index) => (
                                    <div
                                        key={step.id}
                                        className={`relative ${index !== steps.length - 1 ? "md:mb-4" : ""}`}
                                    >
                                        <div className="flex items-start">
                                            {/* Circle */}
                                            <div
                                                className={
                                                    activeStep === step.id && !loopDone
                                                        ? "relative z-10 flex h-10 w-10 scale-110 items-center justify-center rounded-full border border-brand-primary-500 bg-white text-brand-primary-500 transition-all duration-500"
                                                        : "relative z-10 flex h-10 w-10 scale-100 items-center justify-center rounded-full border border-neutralOpacityDark-20 bg-white text-neutral-500 transition-all duration-500"
                                                }
                                            >
                                                {step.id}
                                            </div>

                                            {/* Content */}
                                            <div
                                                className={`${index === steps.length - 1 ? "" : "mb-3"} ml-4 flex-1 text-lg font-semibold leading-[23.4px] md:mb-3 md:ml-6 ${step?.id === 3 ? "flex flex-col self-center" : ""}`}
                                            >
                                                <p
                                                    className={`transition-all duration-500 ${
                                                        activeStep === step.id && !loopDone
                                                            ? "text-xl text-brand-primary-500"
                                                            : "text-base text-neutral-900 md:text-lg"
                                                    }`}
                                                    dangerouslySetInnerHTML={{ __html: step.title }}
                                                ></p>
                                                {step.content && (
                                                    <div className="mt-2 rounded-lg border border-neutralOpacityDark-20 py-2 md:py-4">
                                                        <table className="w-full text-left">
                                                            <tbody>
                                                                {step.content.map(
                                                                    (detail, index) => (
                                                                        <tr
                                                                            key={detail.label}
                                                                            className=""
                                                                        >
                                                                            <td
                                                                                className={`w-[213px] border-r border-neutralOpacityDark-20 px-3 align-top text-sm font-regular leading-[22.4px] tracking-[0.14px] md:text-sm ${
                                                                                    index ===
                                                                                    step.content
                                                                                        .length -
                                                                                        1
                                                                                        ? "pb-0"
                                                                                        : "pb-2 md:pb-4"
                                                                                }`}
                                                                            >
                                                                                {detail.label}
                                                                            </td>
                                                                            <td
                                                                                className={`px-6 align-top text-sm font-semibold leading-[22.4px] tracking-[0.14px] ${
                                                                                    index ===
                                                                                    step.content
                                                                                        .length -
                                                                                        1
                                                                                        ? "pb-0"
                                                                                        : "pb-2 md:pb-4"
                                                                                }`}
                                                                            >
                                                                                {detail.value}
                                                                            </td>
                                                                        </tr>
                                                                    ),
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <RegularButton
                    className="max-w-full text-lg"
                    label="Initiate order"
                    noIcon={true}
                    disabled={!loopDone}
                    showRightIcon={false}
                    onClick={handleOrder}
                    size={isMobile ? ButtonSize.Small : ButtonSize.Large}
                />

                <p className="font-medium text-brand-primary-500">
                    Note: Your order will be processed subject to the realization of payment before{" "}
                    <span className="font-medium text-brand-primary-500">{formattedExpireAt}</span>.
                    In case the payment is not received, the order will get cancelled.
                </p>
            </DialogContent>
        </Dialog>
    );
};

export default NEFTModal;
